<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <h4 class="font-weight-bold">{{ $t("broadcast.customers_title") }}</h4>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="cu-wrap">
          <Card>
            <div slot="body">
              <Spinner v-if="pagesState === 'loading'"
                       size="sm" />
              <div v-else-if="pagesState === 'error'"
                   class="alert alert-warning d-flex align-items-center flex-wrap mb-0 unavailable">
                <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ pagesError || $t("messenger.unavailable") }}</span>
                <button type="button"
                        class="btn btn-outline-primary btn-sm ml-auto"
                        @click="loadPages">{{ $t("messenger.retry") }}</button>
              </div>
              <template v-else>
                <p class="cu-from">
                  <span>{{ $t("broadcast.customers.from_pages", { n: selected.length.toLocaleString("en-US") }) }}</span>
                  <button type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="openPicker">{{ $t("broadcast.customers.change") }}</button>
                </p>

                <CustomerSyncCard v-if="summary"
                                  :sync="summary.sync || []"
                                  :names="names"
                                  :age-seconds="ageSeconds" />

                <div class="cu-filters">
                  <BcChoiceButton v-for="f in filters"
                                  :key="f.key"
                                  :label="f.label"
                                  :count="countOf(f)"
                                  :selected="filter === f.key"
                                  @click="setFilter(f.key)" />
                </div>

                <Spinner v-if="rowsState === 'loading'"
                         size="sm" />
                <div v-else-if="rowsState === 'error'"
                     class="alert alert-warning d-flex align-items-center flex-wrap mb-0 unavailable">
                  <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ rowsError || $t("messenger.unavailable") }}</span>
                  <button type="button"
                          class="btn btn-outline-primary btn-sm ml-auto"
                          @click="loadRows(false)">{{ $t("messenger.retry") }}</button>
                </div>
                <template v-else>
                  <div v-if="rows.length"
                       class="cu-list">
                    <CustomerRow v-for="row in rows"
                                 :key="row.customerId + ':' + row.pageId"
                                 :row="row"
                                 @open="chatRow = $event" />
                  </div>
                  <p v-else
                     class="cu-empty">{{ $t("broadcast.customers.empty") }}</p>
                  <div v-if="moreError"
                       class="alert alert-warning d-flex align-items-center flex-wrap mt-3 mb-0 unavailable">
                    <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ moreError }}</span>
                  </div>
                  <Button v-if="nextCursor"
                          variant="outline-primary"
                          size="lg"
                          custom_class="w-100 mt-3"
                          :loading="loadingMore"
                          :disabled="loadingMore"
                          @click="loadRows(true)">{{ $t("broadcast.customers.more") }}</Button>
                </template>
              </template>
            </div>
          </Card>
        </div>
      </div>
    </section>

    <PagePickerSheet v-if="pickerOpen"
                     v-model="draft"
                     :pages="pages"
                     @apply="applyPages"
                     @close="pickerOpen = false"
                     @reconnect="reconnect" />
    <CustomerChatSheet v-if="chatRow"
                       :row="chatRow"
                       @close="chatRow = null" />
  </div>
</template>

<script>
import Card from "@/components/core/Card";
import Button from "@/components/core/Button";
import Spinner from "@/components/core/Spinner";
import BcChoiceButton from "@/components/derived/broadcast/BcChoiceButton";
import PagePickerSheet from "@/components/derived/broadcast/PagePickerSheet";
import CustomerSyncCard from "@/components/derived/broadcast/CustomerSyncCard";
import CustomerRow from "@/components/derived/broadcast/CustomerRow";
import CustomerChatSheet from "@/components/derived/broadcast/CustomerChatSheet";
import { Toast } from "@/helpers/toastr";

const POLL_MS = 10000;
const PAGE_SIZE = 30;

// ลูกค้าที่เคยทักมา: everyone who ever messaged the chosen Pages. Page row + picker, the 30-day sync card,
// five filters with counts, the customer list and the read-only chat sheet.
export default {
  name: "Seller-Broadcast-Customers",
  components: { Card, Button, Spinner, BcChoiceButton, PagePickerSheet, CustomerSyncCard, CustomerRow, CustomerChatSheet },

  data() {
    return {
      pages: [],
      pagesState: "loading", // loading | ready | error
      pagesError: null,
      selected: [], // applied pageIds
      draft: [], // the picker's working copy; dropped on close, applied on ถัดไป
      pickerOpen: false,
      summary: null,
      summaryAt: 0,
      summaryBusy: false,
      now: Date.now(),
      filter: "all",
      rows: [],
      rowsState: "loading", // loading | ready | error
      rowsError: null,
      nextCursor: null,
      loadingMore: false,
      moreError: null,
      seq: 0, // drops answers of a request a newer one replaced
      chatRow: null,
      timer: null,
    };
  },

  computed: {
    filters() {
      return [
        { key: "tracked", label: this.$t("broadcast.customers.filter_tracked"), count: "tracked" },
        { key: "ordered", label: this.$t("broadcast.customers.filter_ordered"), count: "ordered" },
        { key: "interested", label: this.$t("broadcast.customers.filter_interested"), count: "interested" },
        { key: "one_message", label: this.$t("broadcast.customers.filter_one_message"), count: "one_message" },
        { key: "all", label: this.$t("broadcast.customers.filter_all"), count: "total" },
      ];
    },
    names() {
      const map = {};
      this.pages.forEach((p) => (map[p.pageId] = p.name));
      return map;
    },
    ageSeconds() {
      return this.summaryAt ? Math.max(0, Math.floor((this.now - this.summaryAt) / 1000)) : 0;
    },
    unfinished() {
      return !!this.summary && (this.summary.sync || []).some((it) => it.state === "syncing");
    },
  },

  created() {
    this.loadPages();
    this.timer = setInterval(this.tick, 1000);
  },

  beforeDestroy() {
    clearInterval(this.timer);
  },

  methods: {
    countOf(f) {
      return this.summary ? Number(this.summary[f.count]) || 0 : null;
    },

    tick() {
      this.now = Date.now();
      if (this.summaryBusy || this.now - this.summaryAt < POLL_MS) return;
      // the first answer may have failed: keep asking until there is one, then only while a sync runs
      if (!this.summary || this.unfinished) this.loadSummary();
    },

    loadPages() {
      this.pagesState = "loading";
      this.$store.dispatch("broadcast/fetchPages", {
        callback: (ok, data) => {
          if (this._isDestroyed) return;
          if (!ok) {
            this.pagesError = (data && data.messageTh) || null;
            this.pagesState = "error";
            return;
          }
          this.pages = (data && data.pages) || [];
          this.selected = this.pages.filter((p) => p.eligible).map((p) => String(p.pageId));
          this.pagesState = "ready";
          this.reload();
        },
      });
    },

    reload() {
      this.summary = null;
      this.loadSummary();
      this.loadRows(false);
    },

    loadSummary() {
      if (!this.selected.length) return;
      const pageIds = this.selected;
      this.summaryBusy = true;
      this.$store.dispatch("broadcast/fetchCustomersSummary", {
        pageIds,
        callback: (ok, data) => {
          if (this._isDestroyed) return;
          this.summaryBusy = false;
          if (pageIds !== this.selected) return;
          this.summaryAt = Date.now(); // a failed poll is retried in 10 s, the last answer stays
          this.now = this.summaryAt;
          if (ok) this.summary = data;
        },
      });
    },

    loadRows(more) {
      if (!this.selected.length) {
        this.rows = [];
        this.nextCursor = null;
        this.rowsState = "ready";
        return;
      }
      const seq = ++this.seq;
      if (more) {
        this.loadingMore = true;
        this.moreError = null;
      } else {
        this.rowsState = "loading";
        this.loadingMore = false;
        this.moreError = null;
        this.nextCursor = null;
      }
      this.$store.dispatch("broadcast/fetchCustomers", {
        pageIds: this.selected,
        group: this.filter,
        cursor: more ? this.nextCursor : undefined,
        limit: PAGE_SIZE,
        callback: (ok, data) => {
          if (this._isDestroyed || seq !== this.seq) return;
          this.loadingMore = false;
          if (!ok) {
            const text = (data && data.messageTh) || this.$t("messenger.unavailable");
            if (more) {
              this.moreError = text;
            } else {
              this.rowsError = (data && data.messageTh) || null;
              this.rowsState = "error";
            }
            return;
          }
          const list = (data && data.customers) || [];
          this.rows = more ? this.rows.concat(list) : list;
          this.nextCursor = (data && data.nextCursor) || null;
          this.rowsState = "ready";
        },
      });
    },

    setFilter(key) {
      if (this.filter === key) return;
      this.filter = key;
      this.loadRows(false);
    },

    openPicker() {
      this.draft = this.selected.slice();
      this.pickerOpen = true;
    },

    applyPages(ids) {
      this.selected = ids.map(String);
      this.pickerOpen = false;
      this.reload();
    },

    reconnect() {
      const redirectUrl = window.location.origin + window.location.pathname + "#/settings/shipping";
      this.$store.dispatch("messenger/connectUrl", {
        redirectUrl,
        callback: (ok, url) => {
          if (ok && url) {
            window.location.assign(url);
            return;
          }
          Toast(this.$t("messenger.pages.connect_failed"), "danger");
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cu-wrap {
  max-width: 760px;
  margin: 0 auto;
}

.cu-from {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  color: #666;
  font-size: 0.9375rem;
  margin: 0.1rem 0 0.75rem;
}

.cu-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0 0.5rem;
  margin-bottom: 0.5rem;

  ::v-deep .bc-choice {
    flex: 1 1 9rem;
    width: auto;
  }
}

.cu-empty {
  color: #333;
  font-size: 1.05rem;
  margin: 1rem 0;
}

body.--dark {
  .cu-from {
    color: #c4c4c4;
  }

  .cu-empty {
    color: lightgray;
  }
}
</style>

